】<template>
  <div class="game-container" @keydown="handleKeyPress" tabindex="0">
    <div class="game-header">
      <div class="header-content">
        <h1>复习：{{ title }}</h1>
        <p class="instruction">{{ instruction }}</p>
      </div>
      
      <!-- 添加进度条到顶部 -->
      <div class="progress-row">
        <div class="level-info">进度: {{ Math.round(progress) }}%</div>
        <div class="progress">
          <div class="progress-bar" :style="{ width: progress + '%' }"></div>
        </div>
      </div>
    </div>

    <div class="game-content">
      <div class="stats-container">
        <div class="stat-box score">
          <div class="stat-icon">
            <span class="icon-circle">🎯</span>
          </div>
          <div class="stat-content">
            <div class="stat-value-container">
              <div class="stat-value">{{ score }}</div>
              <div class="stat-label">得分</div>
            </div>
          </div>
          <div class="score-change" v-if="scoreChange !== 0" :class="{ 'positive': scoreChange > 0, 'negative': scoreChange < 0 }">
            {{ scoreChange > 0 ? '+' + scoreChange : scoreChange }}
          </div>
        </div>
        <div class="stat-box accuracy">
          <div class="stat-icon">
            <span class="icon-circle">✨</span>
          </div>
          <div class="stat-content">
            <div class="stat-value-container">
              <div class="stat-value">{{ accuracy }}%</div>
              <div class="accuracy-bar">
                <div class="accuracy-fill" :style="{ width: accuracy + '%' }" :class="{ 'high': accuracy >= 90, 'medium': accuracy >= 70 && accuracy < 90, 'low': accuracy < 70 }"></div>
              </div>
            </div>
            <div class="stat-label">正确率</div>
          </div>
        </div>
      </div>

      <div class="typing-area" :class="{ correct: isCorrect, wrong: isWrong }">
        <div class="target-letters">
          <span 
            v-for="(letter, index) in targetLetters" 
            :key="index"
            :class="{
              'current': index === currentIndex,
              'completed': index < currentIndex
            }"
          >{{ letter.toUpperCase() }}</span>
        </div>
        <div class="letter-instruction">请输入 {{ currentLetter.toUpperCase() }}</div>
      </div>

      <div class="practice-keyboard">
        <div class="practice-row">
          <div 
            v-for="key in ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '=']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': currentLetter.toUpperCase() === key }"
          >{{ key }}</div>
          <div class="practice-key backspace-key">←</div>
        </div>
        <div class="practice-row">
          <div class="practice-key tab-key">Tab</div>
          <div 
            v-for="key in ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', '[', ']']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': currentLetter.toUpperCase() === key }"
          >{{ key }}</div>
          <div class="practice-key backslash-key">\</div>
        </div>
        <div class="practice-row">
          <div class="practice-key caps-key">Caps</div>
          <div 
            v-for="key in ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', ';', '\'']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': currentLetter.toUpperCase() === key }"
          >{{ key }}</div>
          <div class="practice-key enter-key">Enter</div>
        </div>
        <div class="practice-row">
          <div class="practice-key shift-key">Shift</div>
          <div 
            v-for="key in ['Z', 'X', 'C', 'V', 'B', 'N', 'M', ',', '.', '/']" 
            :key="key"
            class="practice-key" 
            :class="{ 'active': currentLetter.toUpperCase() === key }"
          >{{ key }}</div>
          <div class="practice-key shift-key-right">Shift</div>
        </div>
        <div class="practice-row">
          <div class="practice-key ctrl-key">Ctrl</div>
          <div class="practice-key win-key">Win</div>
          <div class="practice-key alt-key">Alt</div>
          <div class="practice-key space-key" :class="{ 'active': currentLetter === ' ' }">空格</div>
          <div class="practice-key alt-key">Alt</div>
          <div class="practice-key win-key">Win</div>
          <div class="practice-key menu-key">≡</div>
          <div class="practice-key ctrl-key">Ctrl</div>
        </div>
      </div>
      
      <!-- 添加退出按钮 -->
      <div class="control-buttons">
        <button @click="exitPractice" class="exit-button">
          <span class="button-icon">🚪</span>
          退出练习
        </button>
      </div>
    </div>
 <!-- 添加打字报告弹窗 -->
 <div class="report-modal" v-if="showComplete">
      <div class="report-content">
        <h2>{{ title }}复习报告</h2>
        <div class="report-stats">
          <div class="stat-item">
            <div class="stat-label">练习用时</div>
            <div class="stat-value">{{ formatTime(totalTime) }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">最终得分</div>
            <div class="stat-value">{{ score }}分</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">正确率</div>
            <div class="stat-value">{{ accuracy }}%</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">打字速度</div>
            <div class="stat-value">{{ typingSpeed }}个/分钟</div>
          </div>
        </div>
        
        <div class="report-message">
          <div class="result-container">
            <img :src="resultImage" :alt="resultMessage">
            <p>{{ resultMessage }}</p>
          </div>
        </div>
        
        <div class="report-buttons">
          <button @click="restartLevel">关闭</button>
        </div>
      </div>
    </div>

    
  </div>
</template>

<script>
export default {
  name: 'TypingPlanetIntroReview',
  data() {
    return {
      gameStarted: true,
      currentLetter: '',
      targetLetters: '',
      currentIndex: 0,
      score: 0,
      totalAttempts: 0,
      correctAttempts: 0,
      accuracy: 100,
      isCorrect: false,
      isWrong: false,
      progress: 0,
      showComplete: false,
      letters: [],
      combinations: 15,
      scoreChange: 0,
      scoreChangeTimeout: null,
      title: '',
      instruction: '',
      levelId: '',
      nextLevel: '',
      startTime: null,
      totalTime: 0,
      typingSpeed: 0,
      resultMessage: '',
      resultImage: '',
      resultImages: {
        excellent: 'https://wugecdn.steam.fun/typefun/images/excellent.png',
        good: 'https://wugecdn.steam.fun/typefun/images/good.png',
        keepTrying: 'https://wugecdn.steam.fun/typefun/images/keep-trying.png'
      }
    }
  },
  methods: {
    generateNewLetter() {
      const length = Math.random() > 0.5 ? 3 : 4
      let newCombination = ''
      for (let i = 0; i < length; i++) {
        const randomLetter = this.letters[Math.floor(Math.random() * this.letters.length)]
        newCombination += randomLetter
      }
      this.targetLetters = newCombination
      this.currentIndex = 0
      this.currentLetter = this.targetLetters[0]
    },

    handleKeyPress(event) {
      if (!this.gameStarted || this.showComplete) return
      
      // 阻止空格键的默认行为（页面滚动）
      if (event.key === ' ' || event.code === 'Space') {
        event.preventDefault()
      }
      
      // 忽略所有特殊键，不计入尝试次数
      const specialKeys = [
        'CapsLock', 'Shift', 'Control', 'Alt', 'Tab', 'Enter', 'Escape', 
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Home', 'End', 'PageUp', 'PageDown', 'Insert', 'Delete',
        'Meta', 'ContextMenu', 'NumLock', 'ScrollLock', 'Pause', 'PrintScreen',
        'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'
      ]
      
      // 检查事件的key值是否在特殊键列表中
      if (specialKeys.includes(event.key) || event.key.startsWith('F') && !isNaN(event.key.slice(1))) {
        return
      }
      
      this.totalAttempts++
      
      if (event.key === this.currentLetter) {
        this.handleCorrectKey()
      } else {
        this.handleWrongKey()
      }
      
      this.accuracy = Math.round((this.correctAttempts / this.totalAttempts) * 100)
    },

    handleCorrectKey() {
      this.isCorrect = true
      this.correctAttempts++
      
      this.showScoreChange(10)
      this.score += 10

      const audio = new Audio('https://wugecdn.steam.fun/typefun/music/correct.mp3')
      audio.play()

      this.currentIndex++
      
      if (this.currentIndex >= this.targetLetters.length) {
        const progressIncrement = 100 / this.combinations
        this.progress += progressIncrement

        if (this.progress >= 100) {
          this.completeLevelAndSave()
        } else {
          setTimeout(() => {
            this.generateNewLetter()
          }, 300)
        }
      } else {
        this.currentLetter = this.targetLetters[this.currentIndex]
      }

      setTimeout(() => {
        this.isCorrect = false
      }, 300)
    },

    handleWrongKey() {
      this.isWrong = true
      
      this.showScoreChange(-5)
      this.score = Math.max(0, this.score - 5)

      const audio = new Audio('https://wugecdn.steam.fun/typefun/music/fail.mp3')
      audio.play()

      setTimeout(() => {
        this.isWrong = false
      }, 300)
    },

    completeLevelAndSave() {
      const completedLevels = JSON.parse(localStorage.getItem('completedLevels') || '[]')
      if (!completedLevels.includes(this.levelId)) {
        completedLevels.push(this.levelId)
        localStorage.setItem('completedLevels', JSON.stringify(completedLevels))
      }
      
      // 计算练习时间和打字速度
      const endTime = new Date()
      this.totalTime = (endTime - this.startTime) / 1000
      this.typingSpeed = Math.round((this.correctAttempts / (this.totalTime / 60)))
      
      // 根据得分和正确率设置结果消息和图片
      if (this.score >= 300 && this.accuracy >= 90) {
        this.resultMessage = '太棒了！你是打字小能手！'
        this.resultImage = this.resultImages.excellent
      } else if (this.score >= 200 && this.accuracy >= 80) {
        this.resultMessage = '做得不错！继续加油！'
        this.resultImage = this.resultImages.good
      } else {
        this.resultMessage = '慢慢来，相信你会做得更好！'
        this.resultImage = this.resultImages.keepTrying
      }
      
      this.showComplete = true
    },

    exitPractice() {
      this.$router.back()
    },

    restartLevel() {
      this.score = 0
      this.totalAttempts = 0
      this.correctAttempts = 0
      this.accuracy = 100
      this.progress = 0
      this.showComplete = false
      this.generateNewLetter()
      this.$el.focus()
      this.startTime = new Date()
      
      // 重置其他状态
      this.isCorrect = false
      this.isWrong = false
      this.scoreChange = 0
      this.typingSpeed = 0
      
      // 确保游戏状态为开始
      this.gameStarted = true
      
      // 清除任何可能存在的计时器
      if (this.scoreChangeTimeout) {
        clearTimeout(this.scoreChangeTimeout)
      }
    },

    showScoreChange(change) {
      this.scoreChange = change
      
      if (this.scoreChangeTimeout) {
        clearTimeout(this.scoreChangeTimeout)
      }
      
      this.scoreChangeTimeout = setTimeout(() => {
        this.scoreChange = 0
      }, 1000)
    },

    loadRouteParams() {
      // 从路由参数获取键位信息
      const { letters, title, instruction, levelId, nextLevel } = this.$route.query
      
      if (letters) {
        // 将字符串中的每个字符作为一个键位
        this.letters = letters.split('')
      } else {
        // 默认键位，以防没有传递参数
        this.letters = ['w', 'o']
      }
      
      this.title = title || 'W和O'
      this.instruction = instruction || '将左手食指放在W键上，右手食指放在O键上'
      this.levelId = levelId || 'intro33'
      this.nextLevel = nextLevel || '/intro/level3'
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.round(seconds % 60)
      return `${minutes}分${remainingSeconds}秒`
    },

    closeReport() {
      this.showComplete = false
      this.$router.back()
    }
  },
  mounted() {
    this.loadRouteParams()
    this.generateNewLetter()
    this.$el.focus()
    this.startTime = new Date()
  },
  // 添加路由参数变化时的处理
  watch: {
    '$route'() {
      this.loadRouteParams()
      this.restartLevel()
    }
  }
}
</script>

<style scoped>
.game-container {
  margin: 0 auto;
  padding: 20px;
  text-align: center;
  outline: none;
  background: #F8FBFF url('https://wugecdn.steam.fun/typefun/images/cartoon-bg.png') repeat;
  font-family: 'Comic Sans MS', 'Marker Felt', 'Arial Rounded MT Bold', sans-serif;
  position: relative;
  overflow: hidden;
}

.game-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('https://wugecdn.steam.fun/typefun/images/stars.png') repeat;
  opacity: 0.1;
  animation: floatBg 60s linear infinite;
  pointer-events: none;
  z-index: 0;
}

@keyframes floatBg {
  from { background-position: 0 0; }
  to { background-position: 100% 100%; }
}

.game-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  padding: 15px 20px;
  border-radius: 20px;
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.05);
  border: 3px solid #E3F2FD;
  margin-bottom: 30px;
  position: relative;
  overflow: hidden;
}

.game-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 6px;
  border-radius: 0 0 20px 20px;
}

.header-content {
  margin-bottom: 15px;
}

.game-header h1 {
  font-size: 28px;
  color: #2196f3;
  font-weight: bold;
  position: relative;
  display: inline-block;
}

.game-header h1::after {
  content: '✨';
  position: absolute;
  right: -30px;
  top: 0;
  font-size: 0.7em;
  animation: twinkle 1.5s infinite alternate;
}

@keyframes twinkle {
  0% { opacity: 0.3; transform: scale(0.8) rotate(0deg); }
  100% { opacity: 1; transform: scale(1.2) rotate(20deg); }
}

.instruction {
  font-size: 16px;
  color: #9e9e9e;
  font-weight: bold;
  position: relative;
  padding: 0 20px;
  margin-top: 20px;
}

.instruction::before, .instruction::after {
  content: '•';
  position: absolute;
  color: #FF9800;
  font-size: 24px;
}

.instruction::before {
  left: 0;
}

.instruction::after {
  right: 0;
}

/* 进度条样式 */
.progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.level-info {
  font-size: 14px;
  color: #666;
  white-space: nowrap;
  flex-shrink: 0;
}

.progress {
  height: 12px;
  background: #E0E0E0;
  border-radius: 20px;
  border: 2px solid #BDBDBD;
  overflow: hidden;
  flex-grow: 1;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}

.progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 75%
  );
  background-size: 20px 20px;
  animation: progressStripes 1s linear infinite;
}

@keyframes progressStripes {
  from { background-position: 0 0; }
  to { background-position: 20px 0; }
}

/* 统计区域样式 */
.stats-container {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 25px;
}

.stat-box {
  background: white;
  padding: 15px 20px;
  border-radius: 20px;
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  flex: 1;
  max-width: 180px;
  position: relative;
  overflow: hidden;
  border: 3px solid #E3F2FD;
}

.stat-box:hover {
  transform: translateY(-5px) scale(1.03);
  border-color: #BBDEFB;
  box-shadow: 0 12px 0 rgba(0, 0, 0, 0.05);
}

/* 图标样式优化 */
.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #E3F2FD;
  font-size: 24px;
  box-shadow: inset 0 -4px 0 rgba(0, 0, 0, 0.1);
  border: 2px solid #BBDEFB;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 26px;
  font-weight: bold;
  color: #FF6B6B;
  text-shadow: 1px 1px 0 #FFE0E0;
}

.stat-label {
  font-size: 14px;
  color: #7986CB;
  font-weight: bold;
}

/* 得分变化动画 */
.score-change {
  position: absolute;
  top: 5px;
  right: 10px;
  font-size: 16px;
  font-weight: bold;
  animation: fadeUp 1s ease-out forwards;
  opacity: 0;
}

.score-change.positive {
  color: #4CAF50;
}

.score-change.negative {
  color: #F44336;
}

@keyframes fadeUp {
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-20px);
  }
}

/* 正确率条样式 */
.stat-value-container {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.accuracy-bar {
  height: 4px;
  background: #eee;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 2px;
}

.accuracy-fill {
  height: 100%;
  transition: width 0.5s ease, background-color 0.5s ease;
}

.accuracy-fill.high {
  background: #4CAF50;
}

.accuracy-fill.medium {
  background: #FFC107;
}

.accuracy-fill.low {
  background: #F44336;
}

/* 打字区域样式 */
.typing-area {
  background: white;
  padding: 30px;
  border-radius: 20px;
  margin: 20px 0;
  transition: all 0.3s;
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  border: 3px solid #E3F2FD;
}

.typing-area::before {
  content: '';
  position: absolute;
  top: -10px;
  right: -10px;
  width: 80px;
  height: 80px;
  background: #FFEB3B;
  opacity: 0.1;
  border-radius: 50%;
  z-index: 0;
}

.typing-area::after {
  content: '';
  position: absolute;
  bottom: -20px;
  left: -20px;
  width: 100px;
  height: 100px;
  background: #FF9800;
  opacity: 0.1;
  border-radius: 50%;
  z-index: 0;
}

.typing-area.correct {
  background: #E8F5E9;
  border-color: #A5D6A7;
  animation: correctPulse 0.5s;
}

@keyframes correctPulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
}

.typing-area.wrong {
  background: #FFEBEE;
  border-color: #FFCDD2;
  animation: wrongShake 0.5s;
}

@keyframes wrongShake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-5px); }
  40%, 80% { transform: translateX(5px); }
}

.target-letters {
  font-size: 48px;
  font-weight: bold;
  margin-bottom: 20px;
  letter-spacing: 15px;
}

.target-letters span {
  display: inline-block;
  padding: 0 10px;
  color: #9E9E9E;
  position: relative;
  transition: all 0.3s;
}

.target-letters span.current {
  color: #2196F3;
  transform: scale(1.2);
  animation: bounce 0.8s infinite alternate;
}

@keyframes bounce {
  0% { transform: translateY(0) scale(1.2); }
  100% { transform: translateY(-10px) scale(1.2); }
}

.target-letters span.completed {
  color: #4CAF50;
  text-shadow: 0 2px 0 rgba(76, 175, 80, 0.2);
}

.letter-instruction {
  font-size: 24px;
  color: #FF6B6B;
  margin: 20px 0;
  font-weight: bold;
  position: relative;
  display: inline-block;
}

.letter-instruction::before, .letter-instruction::after {
  content: '👉';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8em;
  animation: pointPulse 1s infinite alternate;
}

.letter-instruction::before {
  left: -40px;
}

.letter-instruction::after {
  right: -40px;
  transform: translateY(-50%) scaleX(-1);
}

@keyframes pointPulse {
  0% { transform: translateY(-50%) scale(1); }
  100% { transform: translateY(-50%) scale(1.2); }
}

/* 键盘显示区域 */
.practice-keyboard {
  background: linear-gradient(to bottom, #4E5D6C, #364049);
  padding: 25px;
  border-radius: 20px;
  margin: 30px auto;
  box-shadow: 0 15px 0 rgba(54, 64, 73, 0.5), 0 5px 20px rgba(0, 0, 0, 0.2);
  border: 3px solid #5D6D7E;
  transform-style: preserve-3d;
  perspective: 500px;
  transform: rotateX(5deg);
  max-width: 800px;
  width: 100%;
}

.practice-row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}

.practice-key {
  width: 45px;
  height: 45px;
  background: #5D6D7E;
  color: white;
  border: none;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  box-shadow: 0 4px 0 #34495E;
  transition: all 0.15s ease;
  position: relative;
  overflow: hidden;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3);
}

.practice-key::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to bottom, rgba(255,255,255,0.2), rgba(255,255,255,0));
  border-radius: 8px 8px 0 0;
}

.practice-key.active {
  background: #2196f3;
  transform: translateY(4px);
  box-shadow: 0 0 0 #34495E;
  animation: keyPress 0.3s;
}

@keyframes keyPress {
  0% { transform: translateY(0); }
  50% { transform: translateY(4px); }
  100% { transform: translateY(4px); }
}

.practice-key.active::after {
  background: linear-gradient(to bottom, rgba(255,255,255,0.3), rgba(255,255,255,0.1));
}

/* 错开排列，模拟真实键盘 */
.practice-row:nth-child(2) {
  padding-left: 12px;
}

.practice-row:nth-child(3) {
  padding-left: 18px;
}

.practice-row:nth-child(4) {
  padding-left: 25px;
}

/* 特殊键位的宽度 */
.backspace-key {
  width: 70px;
}

.tab-key {
  width: 60px;
}

.backslash-key {
  width: 50px;
}

.caps-key {
  width: 70px;
}

.enter-key {
  width: 75px;
}

.shift-key {
  width: 90px;
}

.shift-key-right {
  width: 90px;
}

.ctrl-key, .alt-key, .win-key, .menu-key {
  width: 50px;
}

.space-key {
  width: 250px;
  font-size: 14px;
}

/* 控制按钮 */
.control-buttons {
  display: flex;
  justify-content: center;
}

.exit-button {
  background: linear-gradient(to bottom, #FF5252, #D32F2F);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 25px;
  font-weight: bold;
  font-size: 16px;
  box-shadow: 0 6px 0 #B71C1C, 0 10px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.exit-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 9px 0 #B71C1C, 0 15px 20px rgba(0, 0, 0, 0.15);
}

.exit-button:active {
  transform: translateY(3px);
  box-shadow: 0 3px 0 #B71C1C, 0 5px 10px rgba(0, 0, 0, 0.1);
}

.exit-button::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to bottom, rgba(255,255,255,0.2), rgba(255,255,255,0));
  border-radius: 50px 50px 0 0;
}

.button-icon {
  margin-right: 10px;
  font-size: 1.2em;
  animation: doorSwing 2s infinite;
  display: inline-block;
  transform-origin: center;
}

@keyframes doorSwing {
  0%, 100% { transform: rotate(0); }
  20% { transform: rotate(-15deg); }
  40% { transform: rotate(10deg); }
  60% { transform: rotate(-5deg); }
  80% { transform: rotate(2deg); }
}

/* 完成界面样式 */
.level-complete-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.level-complete-content {
  background: white;
  padding: 35px;
  border-radius: 8px;
  box-shadow: none;
  border: 1px solid #E3F2FD;
  text-align: center;
  animation: scaleIn 0.3s ease-out;
  max-width: 500px;
  width: 90%;
}

.level-complete-content h2 {
  color: #2196F3;
  font-size: 28px;
  margin-bottom: 20px;
  position: relative;
  display: inline-block;
}

.level-complete-content h2::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 3px;
  background: linear-gradient(90deg, #2196F3, #03A9F4);
  border-radius: 3px;
}

.complete-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin: 25px 0;
}

.complete-stats .stat-item {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.complete-stats .stat-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  background: #f0f7ff;
}

.complete-stats .stat-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(90deg, #2196F3, #03A9F4);
  opacity: 0;
  transition: opacity 0.3s;
}

.complete-stats .stat-item:hover::before {
  opacity: 1;
}

.complete-stats .stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.complete-stats .stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #2196F3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.complete-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 30px;
}

.next-level-button, .restart-button {
  padding: 12px 25px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  text-decoration: none;
}

.next-level-button {
  background: #4CAF50;
  color: white;
}

.next-level-button:hover {
  background: #388E3C;
  transform: none;
  opacity: 0.85;
}

.restart-button {
  background: #FF9800;
  color: white;
}

.restart-button:hover {
  background: #F57C00;
  transform: none;
  opacity: 0.85;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
}

@keyframes scaleIn {
  from { transform: scale(0.98); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

@media (max-width: 768px) {
  .game-container {
    padding: 15px;
  }
  
  .stats-container {
    flex-direction: column;
    align-items: center;
  }
  
  .stat-box {
    max-width: 100%;
    width: 100%;
  }
  
  .typing-area {
    padding: 20px;
  }
  
  .target-letters {
    font-size: 36px;
    letter-spacing: 10px;
  }
  
  /* 移动设备键盘布局优化 */
  .practice-keyboard {
    padding: 15px;
    transform: rotateX(0);
    max-width: 100%;
    overflow-x: auto;
  }
  
  .practice-row {
    margin-bottom: 3px;
    flex-wrap: nowrap;
    min-width: 540px; /* 确保键盘不会被压缩太小 */
  }
  
  .practice-key {
    width: 35px;
    height: 35px;
    font-size: 12px;
    flex-shrink: 0;
  }
  
  /* 移动设备上特殊键的宽度调整 */
  .backspace-key {
    width: 50px;
  }
  
  .tab-key {
    width: 45px;
  }
  
  .backslash-key {
    width: 40px;
  }
  
  .caps-key {
    width: 55px;
  }
  
  .enter-key {
    width: 60px;
  }
  
  .shift-key, .shift-key-right {
    width: 70px;
  }
  
  .ctrl-key, .alt-key, .win-key, .menu-key {
    width: 40px;
  }
  
  .space-key {
    width: 160px;
  }
}

/* 练习报告弹窗优化 */
.report-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.report-content {
  background: white;
  padding: 0;
  border-radius: 30px;
  max-width: 600px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: bounceIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 5px solid #E3F2FD;
  position: relative;
}

@keyframes bounceIn {
  0% { transform: scale(0.8); opacity: 0; }
  70% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}

.report-content h2 {
  font-size: 30px;
  margin: 0;
  padding: 30px 0;
  color: white;
  background: linear-gradient(135deg, #FF9800, #FF5722);
  border-radius: 24px 24px 0 0;
  position: relative;
  font-weight: 700;
  text-shadow: 0 2px 0 rgba(0, 0, 0, 0.2);
}

.report-content h2::before {
  content: '🏆';
  position: absolute;
  left: 30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2em;
  animation: trophyBounce 1s infinite alternate;
}

.report-content h2::after {
  content: '🏆';
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2em;
  animation: trophyBounce 1s infinite alternate;
  animation-delay: 0.5s;
}

@keyframes trophyBounce {
  0% { transform: translateY(-50%) scale(1); }
  100% { transform: translateY(-50%) scale(1.2); }
}

.report-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 30px;
  position: relative;
}

.report-stats::before {
  content: '';
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><circle cx="10" cy="10" r="2" fill="%23f0f0f0"/><circle cx="30" cy="10" r="2" fill="%23f0f0f0"/><circle cx="50" cy="10" r="2" fill="%23f0f0f0"/><circle cx="70" cy="10" r="2" fill="%23f0f0f0"/><circle cx="90" cy="10" r="2" fill="%23f0f0f0"/><circle cx="10" cy="30" r="2" fill="%23f0f0f0"/><circle cx="30" cy="30" r="2" fill="%23f0f0f0"/><circle cx="50" cy="30" r="2" fill="%23f0f0f0"/><circle cx="70" cy="30" r="2" fill="%23f0f0f0"/><circle cx="90" cy="30" r="2" fill="%23f0f0f0"/><circle cx="10" cy="50" r="2" fill="%23f0f0f0"/><circle cx="30" cy="50" r="2" fill="%23f0f0f0"/><circle cx="50" cy="50" r="2" fill="%23f0f0f0"/><circle cx="70" cy="50" r="2" fill="%23f0f0f0"/><circle cx="90" cy="50" r="2" fill="%23f0f0f0"/><circle cx="10" cy="70" r="2" fill="%23f0f0f0"/><circle cx="30" cy="70" r="2" fill="%23f0f0f0"/><circle cx="50" cy="70" r="2" fill="%23f0f0f0"/><circle cx="70" cy="70" r="2" fill="%23f0f0f0"/><circle cx="90" cy="70" r="2" fill="%23f0f0f0"/><circle cx="10" cy="90" r="2" fill="%23f0f0f0"/><circle cx="30" cy="90" r="2" fill="%23f0f0f0"/><circle cx="50" cy="90" r="2" fill="%23f0f0f0"/><circle cx="70" cy="90" r="2" fill="%23f0f0f0"/><circle cx="90" cy="90" r="2" fill="%23f0f0f0"/></svg>');
  z-index: -1;
  opacity: 0.3;
}

.stat-item {
  padding: 20px 15px;
  background: #f8f9ff;
  border-radius: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  border: 2px solid #f0f0f0;
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

@keyframes fadeInUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.stat-item:nth-child(1) { animation-delay: 0.1s; }
.stat-item:nth-child(2) { animation-delay: 0.2s; }
.stat-item:nth-child(3) { animation-delay: 0.3s; }
.stat-item:nth-child(4) { animation-delay: 0.4s; }

.stat-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 13px 0 rgba(0, 0, 0, 0.05);
  border-color: #BBDEFB;
}

.stat-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #4A6FFF, #9C4AFF);
  opacity: 0;
  transition: opacity 0.3s;
  border-radius: 3px 3px 0 0;
}

.stat-item:hover::before {
  opacity: 1;
}

.stat-label {
  font-size: 15px;
  color: #666;
  margin-bottom: 10px;
  position: relative;
  display: inline-block;
  font-weight: 600;
  text-align: center;
}

.stat-label::before {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #ddd;
  border-radius: 2px;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #4A6FFF;
  text-shadow: 0 2px 4px rgba(74, 111, 255, 0.2);
  position: relative;
  display: inline-block;
  text-align: center;
  margin-top: 5px;
}

.keyword-summary {
  margin: 0 30px 30px;
  padding: 20px;
  background: #f0f7ff;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 2px dashed #e0e7ff;
  animation: fadeIn 0.6s ease-out 0.5s forwards;
  opacity: 0;
}

.keyword-summary h3 {
  color: #4A6FFF;
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 700;
}

.keyword-summary p {
  margin: 0;
  color: #666;
  font-size: 16px;
  font-weight: 500;
}

.keyword-summary .highlight {
  background: linear-gradient(135deg, #4A6FFF, #9C4AFF);
  background-clip: text;
    -webkit-background-clip: text;
    background-clip: text;
  color: transparent;
  font-weight: 700;
  padding: 0 5px;
  position: relative;
}

.keyword-summary .highlight::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #4A6FFF, #9C4AFF);
  opacity: 0.2;
  border-radius: 3px;
}

.report-message {
  margin: 0 30px 30px;
  padding: 25px 20px;
  background: #f0f7ff;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 2px dashed #BBDEFB;
  animation: fadeIn 0.6s ease-out 0.5s forwards;
  opacity: 0;
  text-align: center;
}

.report-message::before {
  content: '';
  position: absolute;
  top: -20px;
  right: -20px;
  width: 80px;
  height: 80px;
  background: #4A6FFF;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message::after {
  content: '';
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 100px;
  height: 100px;
  background: #9C4AFF;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message .result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.report-message img {
  width: 150px;
  height: 150px;
  margin-bottom: 15px;
  filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.2));
}

.report-message p {
  font-size: 24px;
  color: #2196f3;
  font-weight: bold;
  text-shadow: 1px 1px 0 #FFE0E0;
  position: relative;
  display: inline-block;
}

.report-message p::before, .report-message p::after {
  content: '✨';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8em;
  animation: twinkle 1.5s infinite alternate;
}

.report-message p::before {
  left: -30px;
  animation-delay: 0.2s;
}

.report-message p::after {
  right: -30px;
  animation-delay: 0.7s;
}

.report-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  padding: 0 30px 30px;
  animation: fadeIn 0.6s ease-out 0.7s forwards;
  opacity: 0;
}

.report-buttons button {
  padding: 15px 30px;
  font-size: 18px;
  font-weight: bold;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  border: none;
  cursor: pointer;
  min-width: 140px;
}

.report-buttons button:first-child {
  background: linear-gradient(to bottom, #4CAF50, #388E3C);
  color: white;
  border: none;
}

.report-buttons button:first-child:hover {
  transform: translateY(-5px);
  box-shadow: 0 13px 0 rgba(0, 0, 0, 0.1), 0 15px 20px rgba(0, 0, 0, 0.15);
}

.report-buttons button:first-child:active {
  transform: translateY(3px);
  box-shadow: 0 5px 0 rgba(0, 0, 0, 0.1), 0 5px 10px rgba(0, 0, 0, 0.1);
}

.report-buttons button:first-child::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to bottom, rgba(255,255,255,0.2), rgba(255,255,255,0));
  border-radius: 50px 50px 0 0;
}

.report-buttons button:last-child {
  background: white;
  color: #666;
  border: 2px solid #eee;
}

.report-buttons button:last-child:hover {
  background: #f8f9ff;
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.05);
  border-color: #e0e7ff;
  color: #4A6FFF;
}

.report-buttons button:last-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.03);
}

.report-buttons button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
  transform: translateY(-100%);
  transition: transform 0.6s;
}

.report-buttons button:hover::before {
  transform: translateY(0);
}

/* 添加一些卡通元素 */
.game-content {
  position: relative;
}

.game-content::before {
  content: '🚀';
  position: absolute;
  top: 20px;
  left: 20px;
  font-size: 30px;
  animation: rocketFloat 5s infinite alternate;
  z-index: 1;
}

.game-content::after {
  content: '💫';
  position: absolute;
  bottom: 40px;
  right: 40px;
  font-size: 30px;
  animation: starSpin 4s infinite linear;
  z-index: 1;
}

@keyframes rocketFloat {
  0% { transform: translateY(0) rotate(-30deg); }
  100% { transform: translateY(-20px) rotate(-25deg); }
}

@keyframes starSpin {
  0% { transform: rotate(0); }
  100% { transform: rotate(360deg); }
}

/* 移动设备适配优化 */
@media (max-width: 768px) {
  .game-header h1 {
    font-size: 24px;
  }
  
  .instruction {
    font-size: 16px;
  }
  
  .target-letters {
    font-size: 36px;
    letter-spacing: 10px;
  }
  
  .letter-instruction {
    font-size: 20px;
  }
  
  .letter-instruction::before, .letter-instruction::after {
    display: none;
  }
  
  .practice-keyboard {
    padding: 15px;
    transform: rotateX(0);
  }
  
  .practice-key {
    width: 35px;
    height: 35px;
    font-size: 12px;
  }
  
  .game-content::before, .game-content::after {
    display: none;
  }
}
</style> 